import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  IconButtonProps,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  getGenerateReportProps,
  useGenerateReportButton,
  UseGenerateReportButtonProps,
} from "./useGenerateReportButton";
import { Space } from "~/lib/types";
import { GenerateReportDialog } from "./GenerateReportDialog";
import { ReportingIcon } from "~/icons";
import { useCanGenerateDocument } from "~/lib/iam/actions/mondoo/integrations/DocumentManager/GenerateDocument";

type GenerateReportButtonProps = UseGenerateReportButtonProps &
  IconButtonProps & {
    renderTrigger?: (handleOpen: () => void) => ReactNode;
  };

export function GenerateReportButton({
  space,
  documentType,
  title,
  frameworkMrn,
  controlMrn,
  assetMrn,
  IconProps = { fontSize: "inherit" },
  advisoryId,
  vulnerabilityId,
  checkId,
  renderTrigger,
  ...props
}: GenerateReportButtonProps) {
  const canGenerateDocument = useCanGenerateDocument(space);

  // Early return if user doesn't have permission to generate documents
  if (!canGenerateDocument) {
    return null;
  }

  const { exportDialog, finishedDialog, form } = useGenerateReportButton(
    getGenerateReportProps({
      documentType,
      space,
      title,
      controlMrn,
      frameworkMrn,
      assetMrn,
      advisoryId,
      vulnerabilityId,
      checkId,
    }),
  );

  return (
    <>
      {renderTrigger ? (
        renderTrigger(exportDialog.handleOpen)
      ) : (
        <Tooltip title="Generate report" placement="top" arrow>
          <IconButton onClick={exportDialog.handleOpen} {...props}>
            <ReportingIcon {...IconProps} />
          </IconButton>
        </Tooltip>
      )}

      <GenerateReportDialog
        open={exportDialog.exportDialogOpen}
        onClose={exportDialog.handleClose}
        title={exportDialog.title}
        isComplianceReport={exportDialog.isComplianceReport}
        form={form}
      />

      <FinishedDialog
        space={space}
        open={finishedDialog.finishedDialogOpen}
        handleClose={finishedDialog.handleFinishedClose}
      />
    </>
  );
}

export function GenerateReportMenuItem({
  space,
  documentType,
  title,
  frameworkMrn,
  controlMrn,
  assetMrn,
  advisoryId,
  vulnerabilityId,
  checkId,
}: UseGenerateReportButtonProps) {
  const canGenerateDocument = useCanGenerateDocument(space);

  if (!canGenerateDocument) {
    return null;
  }

  const { exportDialog, finishedDialog, form } = useGenerateReportButton(
    getGenerateReportProps({
      documentType,
      space,
      title,
      controlMrn,
      frameworkMrn,
      assetMrn,
      advisoryId,
      vulnerabilityId,
      checkId,
    }),
  );

  return (
    <>
      <MenuItem onClick={exportDialog.handleOpen}>
        <ListItemIcon>
          <ReportingIcon fontSize="inherit" />
        </ListItemIcon>
        <ListItemText>Generate report</ListItemText>
      </MenuItem>

      <GenerateReportDialog
        open={exportDialog.exportDialogOpen}
        onClose={exportDialog.handleClose}
        title={exportDialog.title}
        isComplianceReport={exportDialog.isComplianceReport}
        form={form}
      />

      <FinishedDialog
        space={space}
        open={finishedDialog.finishedDialogOpen}
        handleClose={finishedDialog.handleFinishedClose}
      />
    </>
  );
}

type FinishedDialogProps = {
  space: Space;
  open: boolean;
  handleClose: () => void;
};

function FinishedDialog({ space, open, handleClose }: FinishedDialogProps) {
  return (
    <Dialog open={open} maxWidth="md" onClose={handleClose}>
      <DialogTitle
        component="div"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pt: 5,
          px: 5,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            textTransform: "uppercase",
            fontWeight: "700",
            display: "flex",
            alignItems: "center",
          }}
        >
          You're all set!
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pb: 5, px: 5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <Typography sx={{ my: 3 }}>
            When your report is ready, you can download it from the Reporting
            page.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to={`/space/compliance/reports?spaceId=${space.id}`}
          >
            View Reports
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
